import { useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  Brain,
  Cable,
  ChartColumn,
  Check,
  ChevronsUpDown,
  ClipboardList,
  LayoutDashboard,
  LoaderCircle,
  LogOut,
  MessageSquare,
  Plus,
  Settings,
  Zap,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/_shared/components/ui/sidebar';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { FeatureFlagsTrigger } from '@/_shared/feature-flags/FeatureFlagsTrigger';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { cn } from '@/_shared/lib/utils';

import { type ContextHubItem, getContextItemPath } from '../lib/context-hub';
import { RudderstackBrand } from './RudderstackBrand';

interface RecentItem {
  id: string;
  title: string;
  type: 'chat' | 'dashboard' | 'workflow' | 'context' | 'plan';
  to: string;
  updatedAt: string;
  icon: LucideIcon;
}

export function AppSidebar(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const { slug, workspace, wp } = useWorkspace();
  const { user, workspaces, logout } = useAuth();
  const orpc = useOrpc();

  const isCollapsed = state === 'collapsed';

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    ...orpc.chat.list.queryOptions({ input: {} }),
    enabled: !isCollapsed,
  });
  const { data: dashboardsData, isLoading: dashboardsLoading } = useQuery({
    ...orpc.dashboard.list.queryOptions({ input: {} }),
    enabled: !isCollapsed,
  });
  const { data: workflowsData, isLoading: workflowsLoading } = useQuery({
    ...orpc.workflow.list.queryOptions({ input: {} }),
    enabled: !isCollapsed,
  });
  const { data: contextHubData, isLoading: contextHubLoading } = useQuery({
    ...orpc.contextHub.list.queryOptions({ input: {} }),
    enabled: !isCollapsed,
  });
  const { data: plansData, isLoading: plansLoading } = useQuery({
    ...orpc.measurementPlan.list.queryOptions({ input: {} }),
    enabled: !isCollapsed,
  });

  // Path matching — separate list vs detail for nuanced highlighting
  const p = location.pathname;
  const wsPrefix = `/w/${slug}`;
  const isChatListActive = p === wsPrefix || p === `${wsPrefix}/` || p === `${wsPrefix}/chats`;
  const isChatDetailActive = p.startsWith(`${wsPrefix}/chat/`);
  const isDashboardListActive = p === `${wsPrefix}/dashboards`;
  const isDashboardDetailActive = p.startsWith(`${wsPrefix}/d/`);
  const isWorkflowListActive = p === `${wsPrefix}/workflows`;
  const isWorkflowDetailActive = p.startsWith(`${wsPrefix}/workflows/`) && p !== `${wsPrefix}/workflows`;
  const isPlansListActive = p === `${wsPrefix}/plans`;
  const isPlansDetailActive = p.startsWith(`${wsPrefix}/plans/`) && p !== `${wsPrefix}/plans`;
  const isContextActive = p.startsWith(`${wsPrefix}/context`) || p.startsWith(`${wsPrefix}/docs`);
  const isMcpActive = p.startsWith(`${wsPrefix}/mcp`);
  const isSettingsActive = p.startsWith(`${wsPrefix}/settings`);

  // Merged recents: combine all types, sort by updatedAt, take 5
  const recentsLoading = sessionsLoading || dashboardsLoading || workflowsLoading || contextHubLoading || plansLoading;
  const recents = useMemo<RecentItem[]>(() => {
    const chats: RecentItem[] = (sessionsData?.sessions ?? []).slice(0, 5).map((s) => ({
      id: s.id,
      title: s.title ?? 'New conversation',
      type: 'chat' as const,
      to: wp(`/chat/${s.id}`),
      updatedAt: s.updatedAt,
      icon: MessageSquare,
    }));
    const dashboards: RecentItem[] = (dashboardsData?.dashboards ?? []).slice(0, 5).map((d) => ({
      id: d.id,
      title: d.title,
      type: 'dashboard' as const,
      to: wp(`/d/${d.slug}`),
      updatedAt: d.updatedAt,
      icon: LayoutDashboard,
    }));
    const workflows: RecentItem[] = (workflowsData?.workflows ?? []).slice(0, 5).map((w) => ({
      id: w.id,
      title: w.name,
      type: 'workflow' as const,
      to: wp(`/workflows/${w.id}`),
      updatedAt: w.updatedAt,
      icon: Zap,
    }));
    const contextItems: RecentItem[] = (contextHubData?.items ?? []).slice(0, 5).map((item: ContextHubItem) => ({
      id: item.id,
      title: item.title,
      type: 'context' as const,
      to: wp(getContextItemPath(item)),
      updatedAt: item.updatedAt,
      icon: item.kind === 'memory' ? Brain : BookOpen,
    }));
    const plans: RecentItem[] = (plansData?.plans ?? []).slice(0, 5).map((pl) => ({
      id: pl.id,
      title: pl.title,
      type: 'plan' as const,
      to: wp(`/plans/${pl.id}`),
      updatedAt: pl.updatedAt,
      icon: ClipboardList,
    }));
    return [...chats, ...dashboards, ...workflows, ...contextItems, ...plans]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 5);
  }, [sessionsData, dashboardsData, workflowsData, contextHubData, plansData, wp]);

  const handleWorkspaceSwitch = (wsId: string) => {
    if (wsId !== workspace.id) {
      void navigate(`/w/${wsId}`);
    }
  };

  // Nav items highlight on list pages always. On detail pages, they highlight when
  // the current page isn't visible in the recents list (so something is always active).
  // In collapsed mode, recents are hidden, so detail pages always highlight the parent nav.
  const currentInRecents = !isCollapsed && recents.some((r) => r.to === p);
  const navItems = [
    {
      label: 'Chats',
      to: wp('/chats'),
      icon: MessageSquare,
      active: isChatListActive || (!currentInRecents && isChatDetailActive),
    },
    {
      label: 'Dashboards',
      to: wp('/dashboards'),
      icon: LayoutDashboard,
      active: isDashboardListActive || (!currentInRecents && isDashboardDetailActive),
    },
    {
      label: 'Workflows',
      to: wp('/workflows'),
      icon: Zap,
      active: isWorkflowListActive || (!currentInRecents && isWorkflowDetailActive),
    },
    {
      label: 'Plans',
      to: wp('/plans'),
      icon: ClipboardList,
      active: isPlansListActive || (!currentInRecents && isPlansDetailActive),
      badge: 'NEW',
    },
    {
      label: 'Context',
      to: wp('/context'),
      icon: BookOpen,
      active: isContextActive,
    },
  ];

  return (
    <Sidebar variant="floating" collapsible="icon">
      {/* ── Header: brand + workspace + new chat ── */}
      <SidebarHeader className={cn('px-1.5 pt-1.5', isCollapsed ? 'items-center gap-2' : 'gap-2')}>
        <Link
          to={wp()}
          className={cn(
            'transition-colors hover:bg-sidebar-accent/50',
            isCollapsed ? 'flex size-10 items-center justify-center rounded-xl' : 'rounded-md px-2 py-1',
          )}
          aria-label="Go to chat"
        >
          <RudderstackBrand
            variant={isCollapsed ? 'mark' : 'full'}
            label="Rudder AI"
            badge="beta"
            className={cn('min-w-0', isCollapsed && 'justify-center')}
            markClassName={cn(isCollapsed ? 'size-8 rounded-lg' : 'size-7')}
          />
        </Link>

        {/* Workspace switcher — kept as card per plan Step 3 */}
        {isCollapsed ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton className="justify-center" aria-label="Switch workspace">
                <ChartColumn className="size-4 text-primary" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <WorkspaceDropdownContent
              workspaces={workspaces}
              currentId={workspace.id}
              onSwitch={handleWorkspaceSwitch}
              onNew={() => void navigate('/new-workspace')}
            />
          </DropdownMenu>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Switch workspace"
                className="flex w-full items-center gap-2 rounded-lg border border-sidebar-border/70 bg-white/78 px-2.5 py-2 text-left text-sidebar-foreground transition-colors hover:bg-white"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/12 text-primary">
                  <ChartColumn className="size-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[0.625rem] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                    Workspace
                  </span>
                  <span className="block truncate text-[0.8125rem] font-semibold text-foreground">
                    {workspace.name}
                  </span>
                </span>
                <ChevronsUpDown className="size-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <WorkspaceDropdownContent
              workspaces={workspaces}
              currentId={workspace.id}
              onSwitch={handleWorkspaceSwitch}
              onNew={() => void navigate('/new-workspace')}
            />
          </DropdownMenu>
        )}

        {isCollapsed ? (
          <SidebarMenuButton
            asChild
            tooltip="New Chat"
            className="justify-center bg-primary text-primary-foreground hover:bg-primary/94 hover:text-primary-foreground"
          >
            <Link to={wp()} aria-label="New chat">
              <MessageSquare className="size-4" />
            </Link>
          </SidebarMenuButton>
        ) : (
          <Button
            variant="default"
            size="sm"
            asChild
            className="h-9 w-full justify-center rounded-lg text-sm font-semibold"
          >
            <Link to={wp()} aria-label="New chat">
              <MessageSquare className="size-4" />
              <span>New Chat</span>
            </Link>
          </Button>
        )}
      </SidebarHeader>

      {/* ── Content: flat nav + merged recents ── */}
      <SidebarContent className={cn('px-1.5', isCollapsed && 'items-center')}>
        <SidebarGroup className="p-0">
          <SidebarMenu>
            {navItems.map((item) => (
              <SidebarMenuItem key={item.label}>
                <SidebarMenuButton asChild isActive={item.active} tooltip={item.label}>
                  <Link to={item.to} aria-current={item.active ? 'page' : undefined}>
                    <item.icon className="size-4" />
                    <span>{item.label}</span>
                    {'badge' in item && item.badge && (
                      <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[0.5625rem] leading-none font-semibold text-primary-foreground">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        {/* Merged recents — hidden when collapsed */}
        {!isCollapsed && (
          <SidebarGroup className="p-0">
            <SidebarGroupLabel>Recents</SidebarGroupLabel>
            <SidebarGroupContent>
              {recentsLoading ? (
                <div role="status" className="flex items-center justify-center py-2 text-muted-foreground">
                  <LoaderCircle className="size-4 animate-spin" />
                  <span className="sr-only">Loading recents</span>
                </div>
              ) : recents.length > 0 ? (
                <SidebarMenu>
                  {recents.map((item) => (
                    <SidebarMenuItem key={`${item.type}-${item.id}`}>
                      <SidebarMenuButton asChild size="sm" isActive={p === item.to} className="text-muted-foreground">
                        <Link to={item.to} aria-current={p === item.to ? 'page' : undefined}>
                          <item.icon className="size-3.5" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              ) : null}
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* ── Footer: settings nav item + user row ── */}
      <SidebarFooter className={cn('px-1.5 pb-1.5', isCollapsed ? 'items-center gap-1' : 'gap-0')}>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isMcpActive} tooltip="MCP">
              <Link to={wp('/mcp')} aria-current={isMcpActive ? 'page' : undefined}>
                <Cable className="size-4" />
                <span>MCP</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isSettingsActive} tooltip="Settings">
              <Link to={wp('/settings')} aria-current={isSettingsActive ? 'page' : undefined}>
                <Settings className="size-4" />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <FeatureFlagsTrigger />
        </SidebarMenu>

        {user && (
          <>
            <SidebarSeparator />
            {isCollapsed ? (
              <div className="flex flex-col items-center gap-1">
                <Link to={wp('/settings/members')} aria-label={`${user.githubLogin} account`}>
                  <Avatar className="size-6 border border-border/70">
                    <AvatarImage src={user.githubAvatarUrl ?? undefined} alt={user.githubLogin} />
                    <AvatarFallback className="text-[0.625rem]">{user.githubLogin[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Link>
                <button
                  type="button"
                  onClick={() => void logout()}
                  aria-label="Sign out"
                  title="Sign out"
                  className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                >
                  <LogOut className="size-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-2 py-1">
                <Avatar className="size-6 border border-border/70">
                  <AvatarImage src={user.githubAvatarUrl ?? undefined} alt={user.githubLogin} />
                  <AvatarFallback className="text-[0.625rem]">{user.githubLogin[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium text-sidebar-foreground">
                  {user.githubLogin}
                </span>
                <span className="text-[0.625rem] text-muted-foreground/60 tabular-nums">v{__APP_VERSION__}</span>
                <button
                  type="button"
                  onClick={() => void logout()}
                  title="Sign out"
                  aria-label="Sign out"
                  className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                >
                  <LogOut className="size-3" />
                </button>
              </div>
            )}
          </>
        )}
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}

/** Shared workspace dropdown content used in both expanded and collapsed modes. */
function WorkspaceDropdownContent({
  workspaces,
  currentId,
  onSwitch,
  onNew,
}: {
  workspaces: { id: string; name: string }[];
  currentId?: string;
  onSwitch: (id: string) => void;
  onNew: () => void;
}): React.ReactElement {
  return (
    <DropdownMenuContent className="min-w-56 rounded-lg p-1.5" align="start">
      {workspaces.map((ws) => (
        <DropdownMenuItem key={ws.id} className="rounded-md px-2.5 py-1.5" onClick={() => onSwitch(ws.id)}>
          <span className="truncate">{ws.name}</span>
          {ws.id === currentId && <Check className="ml-auto size-4" />}
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuItem className="rounded-md px-2.5 py-1.5 text-muted-foreground" onClick={onNew}>
        <Plus className="mr-2 size-4" />
        Create workspace
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}
